import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useState } from "react";

export const Route = createFileRoute("/help/quick-start")({
	component: QuickStart,
});

function InitCommand() {
	const command = "npx agentframer@latest init";
	const [copied, setCopied] = useState(false);

	const handleCopy = useCallback(async () => {
		await navigator.clipboard.writeText(command);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	}, []);

	return (
		<button
			type="button"
			onClick={handleCopy}
			aria-label={copied ? "Copied" : "Copy command"}
			className="group flex w-full items-center gap-3 border px-3 py-2.5 text-left font-mono text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
			style={{
				background: "#0a0a0a",
				border: "1px solid rgba(255,255,255,0.1)",
				borderLeft: "3px solid #6B3FF2",
				color: "#e1e4e8",
				fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
			}}
		>
			<span style={{ color: "#6B3FF2" }}>$</span>
			<span className="min-w-0 flex-1">{command}</span>
			{copied ? (
				<span className="material-symbols-outlined shrink-0" aria-hidden="true" style={{ fontSize: "14px", color: "#2b9a66", fontVariationSettings: "'FILL' 1" }}>check_circle</span>
			) : (
				<span className="material-symbols-outlined shrink-0" aria-hidden="true" style={{ fontSize: "14px", color: "#888888" }}>content_copy</span>
			)}
		</button>
	);
}

function TerminalBlock() {
	return (
		<div
			className="overflow-hidden text-sm font-mono"
			style={{
				background: "#0a0a0a",
				border: "1px solid rgba(255,255,255,0.1)",
				borderTop: "3px solid #6B3FF2",
				fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
			}}
		>
			{/* Terminal chrome */}
			<div
				className="flex items-center justify-between px-4 py-2.5"
				style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}
			>
				<div className="flex items-center gap-1.5">
					<div className="w-2.5 h-2.5 rounded-full" style={{ background: "#ff5f57" }} />
					<div className="w-2.5 h-2.5 rounded-full" style={{ background: "#febc2e" }} />
					<div className="w-2.5 h-2.5 rounded-full" style={{ background: "#28c840" }} />
				</div>
				<span
					className="text-xs tracking-wide"
					style={{ fontFamily: "inherit", color: "#888888" }}
				>
					Terminal, zsh
				</span>
			</div>
			{/* Terminal body */}
			<div className="px-4 py-4 space-y-1" style={{ color: "#888888" }}>
				<div>
					<span style={{ color: "#6B3FF2" }}>$ </span>
					<span style={{ color: "#e1e4e8" }}>npx agentframer@latest init</span>
				</div>
				<div style={{ color: "#2b9a66" }}>✓ Config created in .agentframer/config.json</div>
				<div style={{ color: "#2b9a66" }}>✓ MCP local bridge started on port 4550</div>
			</div>
		</div>
	);
}

function InfoCallout({
	title,
	children,
}: {
	title: string;
	children: React.ReactNode;
}) {
	return (
		<div
			className="px-4 py-4 text-sm leading-relaxed"
			style={{
				background: "rgba(234,40,4,0.02)",
				border: "1px solid rgba(234,40,4,0.1)",
				borderLeft: "3px solid #6B3FF2",
			}}
		>
			<div className="flex items-start gap-3">
				<span
					className="material-symbols-outlined shrink-0 mt-0.5"
					aria-hidden="true"
					style={{ fontSize: "16px", color: "#6B3FF2", fontVariationSettings: "'FILL' 1" }}
				>
					info
				</span>
				<div>
					<div className="font-bold mb-1" style={{ color: "#6B3FF2", fontFamily: "inherit" }}>
						{title}
					</div>
					<div style={{ color: "var(--km-on-surface-muted)" }}>{children}</div>
				</div>
			</div>
		</div>
	);
}

function SectionNumber({ n }: { n: string }) {
	return (
		<span
			className="text-xs font-bold tracking-[0.1em] mr-2"
			style={{
				fontFamily: "inherit",
				color: "#6B3FF2",
			}}
		>
			{n}
		</span>
	);
}

export function QuickStart() {
	return (
		<article className="space-y-12">
			{/* Breadcrumb */}
			<div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.1em]" style={{ fontFamily: "inherit" }}>
				<Link
					to="/help"
					className="transition-colors hover:text-[var(--km-on-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
					style={{ color: "var(--km-on-surface-muted)" }}
				>
					Docs
				</Link>
				<span style={{ color: "rgba(255,255,255,0.12)" }}>›</span>
				<span style={{ color: "#6B3FF2" }}>Quickstart</span>
			</div>

			{/* Hero */}
			<div className="space-y-3">
				<h1
					className="text-4xl md:text-5xl font-semibold tracking-tight"
					style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
				>
					Quickstart
				</h1>
				<p className="text-sm leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
					Get your AI agent generating images, video, and audio in under five minutes using the
					Model Context Protocol.
				</p>
			</div>

			{/* Section 01, Setting up MCP */}
			<section id="setting-up-mcp" className="space-y-4">
				<h2
					className="text-xl font-semibold flex items-center"
					style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
				>
					<SectionNumber n="01" />
					Setting up MCP
				</h2>
				<p className="text-sm leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
					MCP (Model Context Protocol) is the open protocol that lets your AI assistant talk
					directly to AgentFramer's generation engine. Install the CLI and initialize the local
					bridge, that's all the wiring you need.
				</p>
				<TerminalBlock />
				<InfoCallout title="How jobs work">
					AgentFramer handles job queuing server-side, your agent calls a generation tool and
					gets back a job ID. It then polls for completion and retrieves the output URL when
					ready. No async infrastructure required on your end.
				</InfoCallout>
				<div className="pt-1">
					<InitCommand />
				</div>
			</section>

			{/* Section 02, Connecting your Agent */}
			<section id="connecting-your-agent" className="space-y-4">
				<h2
					className="text-xl font-semibold flex items-center"
					style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
				>
					<SectionNumber n="02" />
					Connecting your Agent
				</h2>
				<p className="text-sm leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
					Once the bridge is running, register the AgentFramer MCP toolset with your AI client.
					You can connect via a locally running bridge for development, or point straight to our
					managed cloud infrastructure for production workloads.
				</p>
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
					<div
						className="p-6 flex flex-col gap-3"
						style={{
							background: "var(--km-surface-high)",
							border: "1px solid rgba(255,255,255,0.08)",
							borderTop: "3px solid #6B3FF2",
						}}
					>
						<span className="material-symbols-outlined text-2xl" aria-hidden="true" style={{ color: "#6B3FF2" }}>
							link
						</span>
						<div>
							<div
								className="font-bold text-sm mb-1"
								style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
							>
								Local Bridge
							</div>
							<div className="text-xs leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
								Connect to a locally running bridge for development and fast iteration cycles.
							</div>
						</div>
					</div>
					<div
						className="p-6 flex flex-col gap-3"
						style={{
							background: "var(--km-surface-high)",
							border: "1px solid rgba(255,255,255,0.08)",
							borderTop: "3px solid #6B3FF2",
						}}
					>
						<span className="material-symbols-outlined text-2xl" aria-hidden="true" style={{ color: "#6B3FF2" }}>
							cloud
						</span>
						<div>
							<div
								className="font-bold text-sm mb-1"
								style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
							>
								Managed Cloud
							</div>
							<div className="text-xs leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
								Scale to production with our hosted MCP infrastructure.
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Section 03, Your First Generation */}
			<section id="your-first-generation" className="space-y-4">
				<h2
					className="text-xl font-semibold flex items-center"
					style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
				>
					<SectionNumber n="03" />
					Your First Generation
				</h2>
				<p className="text-sm leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
					With the bridge connected, your AI assistant has access to{" "}
					<code
						className="px-1.5 py-0.5 text-xs"
						style={{
							backgroundColor: "rgba(234,40,4,0.08)",
							border: "1px solid rgba(234,40,4,0.15)",
							borderLeft: "2px solid #6B3FF2",
							color: "#6B3FF2",
							fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
						}}
					>
						generate_image
					</code>
					,{" "}
					<code
						className="px-1.5 py-0.5 text-xs"
						style={{
							backgroundColor: "rgba(234,40,4,0.08)",
							border: "1px solid rgba(234,40,4,0.15)",
							borderLeft: "2px solid #6B3FF2",
							color: "#6B3FF2",
							fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
						}}
					>
						generate_video
					</code>
					, and{" "}
					<code
						className="px-1.5 py-0.5 text-xs"
						style={{
							backgroundColor: "rgba(234,40,4,0.08)",
							border: "1px solid rgba(234,40,4,0.15)",
							borderLeft: "2px solid #6B3FF2",
							color: "#6B3FF2",
							fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
						}}
					>
						generate_audio
					</code>
					. Just describe what you want, the agent handles model selection, queuing, and delivery.
				</p>
				<div
					className="p-4 space-y-1.5 text-xs"
					style={{
						background: "#0a0a0a",
						border: "1px solid rgba(255,255,255,0.1)",
						borderLeft: "3px solid #6B3FF2",
						fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
						color: "#e1e4e8",
					}}
				>
					{[
						"Generate an image of a red sports car at sunset",
						"Which image, video, and audio models are available right now?",
						"Generate an image with nano-banana-2 at 1024 by 1024",
						"Upscale that image to 4 megapixels",
						"Generate a 5 second video of waves rolling onto a black sand beach",
						"Generate a 30 second ambient synth track for a futuristic product teaser",
						"Generate a square product image of a matte ceramic mug on white",
						"Show me my recent generations",
						"Open the last generated video",
						"How much balance do I have left?",
						"List my workspaces",
						"Create a workspace called Client Launch",
						"Switch to workspace wksp_123",
						"Invite alex@example.com to this workspace",
						"Show members and pending invites for this workspace",
					].map((prompt) => (
						<div key={prompt} className="flex items-start gap-2">
							<span style={{ color: "#6B3FF2" }}>›</span>
							<span>&ldquo;{prompt}&rdquo;</span>
						</div>
					))}
				</div>
				<p className="text-sm leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
					Your AI assistant calls AgentFramer&apos;s tools on your behalf, no dashboard
					required. See the{" "}
					<Link
						to="/help/mcp"
						className="rpl-link transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
						style={{ color: "#6B3FF2", textDecoration: "underline dotted #6B3FF2" }}
					>
						MCP overview
					</Link>{" "}
					for a full list of available tools and capabilities.
				</p>
			</section>

			{/* Footer feedback + navigation */}
			<div
				className="flex flex-col gap-6 pt-4"
				style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
			>
				<div className="flex items-center gap-3">
					<span className="text-xs" style={{ color: "var(--km-on-surface-muted)", fontFamily: "inherit" }}>
						Was this page helpful?
					</span>
					<button
						type="button"
						aria-label="Mark as helpful"
						className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
						style={{
							backgroundColor: "rgba(234,40,4,0.08)",
							border: "1px solid #6B3FF2",
							borderLeft: "2px solid #6B3FF2",
							color: "#6B3FF2",
						}}
					>
						<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px" }}>thumb_up</span>
						Yes
					</button>
					<button
						type="button"
						aria-label="Mark as not helpful"
						className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
						style={{
							background: "var(--km-surface-high)",
							color: "var(--km-on-surface-muted)",
							border: "1px solid rgba(255,255,255,0.08)",
						}}
					>
						<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px" }}>thumb_down</span>
						No
					</button>
				</div>
				<div className="flex items-center justify-between text-sm">
					<Link
						to="/help/what-is-agentframer"
						className="flex items-center gap-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
						style={{ color: "var(--km-on-surface-muted)", textDecoration: "none" }}
						onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = "var(--km-on-surface)"; (e.currentTarget as HTMLElement).style.textDecoration = "underline dotted"; }}
						onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = "var(--km-on-surface-muted)"; (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
						onFocus={e => { (e.currentTarget as HTMLElement).style.color = "var(--km-on-surface)"; (e.currentTarget as HTMLElement).style.textDecoration = "underline dotted"; }}
						onBlur={e => { (e.currentTarget as HTMLElement).style.color = "var(--km-on-surface-muted)"; (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
					>
						<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>arrow_back</span>
						How it works
					</Link>
					<Link
						to="/help/inviting-members"
						className="flex items-center gap-1.5 transition-colors font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
						style={{ color: "#6B3FF2", textDecoration: "none" }}
						onMouseEnter={e => { (e.currentTarget as HTMLElement).style.textDecoration = "underline dotted"; }}
						onMouseLeave={e => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
						onFocus={e => { (e.currentTarget as HTMLElement).style.textDecoration = "underline dotted"; }}
						onBlur={e => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
					>
						Inviting members
						<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>arrow_forward</span>
					</Link>
				</div>
			</div>
		</article>
	);
}
